import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser'
import { WebService, lastValueFrom, NavTabService, PromptService, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import { Column } from '@interfaces';
import { DashboardRefreshService } from '../../dashboard-refresh.service';
import { GridSizeRules } from '../../dashboard.types';

export const SUPPLIER_REQUEST_RULES: GridSizeRules = {
  minCols: 4, maxCols: 12,
  minRows: 4, maxRows: 16
};

// Optional: define a type so you get intellisense + safer columns
export interface SupplierRequest {
  SupplierRequestJSON?: string | Record<string, any>;

  // ---- Core / meta ----
  responder?: string;
  SupplierID?: string;              // may be blank string
  SupplierRequestID?: number | string; // used when navigating (optional)

  // ---- Basic Info ----
  CompanyName?: string;
  Country?: string;
  FDARegistration?: string;
  FLOID?: string;
  FTUSAID?: string;

  PSS1Email?: string;
  PSS2Email?: string;
  PSS3Email?: string;

  // ---- Trader Contact ----
  TraderContactFirstName?: string;
  TraderContactLastName?: string;
  TraderContactEmail?: string;
  TraderPhone?: string;

  TraderStreetAddress?: string;
  TraderCity?: string;
  TraderState?: string;
  TraderPostalCode?: string;

  TraderMessenger?: string;

  TraderSecondaryEmail1?: string;
  TraderSecondaryEmail2?: string;
  TraderSecondaryEmail3?: string;

  // ---- Traffic ----
  TrafficFirstName?: string;
  TrafficLastName?: string;
  TrafficEmail?: string;
  TrafficPhone?: string;

  TrafficSecondaryEmail1?: string;
  TrafficSecondaryEmail2?: string;
  TrafficSecondaryEmail3?: string;

  // ---- Price Fix ----
  PriceFixFirstName?: string;
  PriceFixLastName?: string;
  PriceFixEmail?: string;
  PriceFixPhone?: string;

  PriceFixSecondaryEmail1?: string;
  PriceFixSecondaryEmail2?: string;
  PriceFixSecondaryEmail3?: string;

  // Keep the parsed object too (handy for debugging / details view)
  _parsed?: Record<string, any>;
}

@Component({
  selector: 'app-supplier-requests',
  imports: [RmsModule],
  templateUrl: './supplier-requests.widget.html',
  styleUrl: './supplier-requests.widget.scss'
})
export class SupplierRequestsWidget implements OnInit {
  SupplierRequests: SupplierRequest[] = [];
  RequestColumns: Column[] = [];

  private supplierFieldMap = [
    // --- Basic Info ---
    { req: 'CompanyName', db: 'SupplierName' },
    { req: 'Country', db: 'SupplierContactCountry' },
    { req: 'FDARegistration', db: 'SupplierFDA' },
    { req: 'FLOID', db: 'SupplierFLOID' },
    { req: 'FTUSAID', db: 'SupplierFTUSAID' },
    { req: 'TraderMessenger', db: 'SupplierContactSkypeHandle'},

    { req: 'PSS1Email', db: 'AppRejEmailNo1' },
    { req: 'PSS2Email', db: 'AppRejEmailNo2' },
    { req: 'PSS3Email', db: 'AppRejEmailNo3' },

    // --- Trader Contact ---
    { req: 'TraderContactFirstName', db: 'TraderContactFirstName' },
    { req: 'TraderContactLastName', db: 'TraderContactLastName' },
    { req: 'TraderContactEmail', db: 'SupplierEmail' },
    { req: 'TraderPhone', db: 'SupplierTel' },

    { req: 'TraderStreetAddress', db: 'SupplierStreet' },
    { req: 'TraderCity', db: 'SupplierCity' },
    { req: 'TraderState', db: 'SupplierState' },
    { req: 'TraderPostalCode', db: 'SupplierZip' },

    { req: 'TraderSecondaryEmail1', db: 'TraderSecondaryEmail1' },
    { req: 'TraderSecondaryEmail2', db: 'TraderSecondaryEmail2' },
    { req: 'TraderSecondaryEmail3', db: 'TraderSecondaryEmail3' },

    // --- Traffic ---
    { req: 'TrafficFirstName', db: 'TrafficContactFirstName' },
    { req: 'TrafficLastName', db: 'TrafficContactLastName' },
    { req: 'TrafficEmail', db: 'SupplierTrafficEmail' },
    { req: 'TrafficPhone', db: 'TrafficNo' },

    { req: 'TrafficSecondaryEmail1', db: 'TrafficSecondaryEmail1' },
    { req: 'TrafficSecondaryEmail2', db: 'TrafficSecondaryEmail2' },
    { req: 'TrafficSecondaryEmail3', db: 'TrafficSecondaryEmail3' },

    // --- Price Fix ---
    { req: 'PriceFixFirstName', db: 'PriceFixFirstName' },
    { req: 'PriceFixLastName', db: 'PriceFixLastName' },
    { req: 'PriceFixEmail', db: 'PriceFixEmail' },
    { req: 'PriceFixPhone', db: 'PriceFixPhone' },

    { req: 'PriceFixSecondaryEmail1', db: 'PriceFixSecondaryEmail1' },
    { req: 'PriceFixSecondaryEmail2', db: 'PriceFixSecondaryEmail2' },
    { req: 'PriceFixSecondaryEmail3', db: 'PriceFixSecondaryEmail3' },
  ];

  constructor(private web: WebService, private dashboardRefresh: DashboardRefreshService, private navTab: NavTabService, private prompt: PromptService, private sanitizer: DomSanitizer, private message: RmsMessageService) { }

  async ngOnInit() {
    await this.Refresh();
    this.CreateColumns();

    this.dashboardRefresh.onSupplierRequestsRefresh().subscribe(() => {
      this.Refresh();
    })
  }

  /**
   * Public refresh method so the parent can call it via @ViewChild.
   */
  public async Refresh(): Promise<void> {
    const raw = await lastValueFrom(this.web.getWebCall('venders', 'supplier_requests', {skipLoader: true}));

    // Ensure array
    const rows: any[] = Array.isArray(raw) ? raw : (raw ? [raw] : []);

    // Parse SupplierRequestJSON into row properties
    this.SupplierRequests = rows.map(r => this.hydrateSupplierRequest(r));
  }

  private hydrateSupplierRequest(row: any): SupplierRequest {
    const result: SupplierRequest = { ...row };

    const parsed = this.parseSupplierRequestJSON(row?.SupplierRequestJSON);

    if (parsed) {
      // attach parsed object + spread to top-level properties
      result._parsed = parsed;
      Object.assign(result, parsed);
    } else {
      result._parsed = {};
    }

    // Normalize the 2 columns you want:
    // "Supplier Name" = CompanyName
    // SupplierID may be blank
    result.CompanyName = (result.CompanyName ?? '').toString();
    result.SupplierID = (result.SupplierID ?? '').toString();

    return result;
  }

  private parseSupplierRequestJSON(value: any): Record<string, any> | null {
    if (!value) return null;

    // If backend already returns an object, accept it
    if (typeof value === 'object') return value as Record<string, any>;

    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      return JSON.parse(trimmed);
    } catch {
      // Sometimes JSON is double-encoded or has leading/trailing junk
      // Try a basic cleanup pass (optional but helpful)
      try {
        const cleaned = trimmed
          .replace(/^\uFEFF/, '')              // remove BOM
          .replace(/^[^\[{]*?(\{)/, '$1')      // keep from first {
          .replace(/(\})[^\}]*$/, '$1');       // keep to last }
        return JSON.parse(cleaned);
      } catch {
        return null;
      }
    }
  }

  CreateColumns() {
    this.RequestColumns = [
      { label: 'ID', key: 'SupplierID', width: 20 },
      { label: 'Supplier Name', key: 'CompanyName', width: 80 },
    ];
  }
  async onSelectRequest(request: any) {
    console.log('Selected request:', request);


    const htmlPrompt = await this.createHtml(request, { titlePrefix: 'Supplier Request Details' });

    const { closed$ } = this.prompt.open({
      htmlPrompt: this.sanitizer.bypassSecurityTrustHtml(htmlPrompt),
      buttons: [{ value: 1, label: 'Auto Approve', buttonClass: 'btn-bg-green' }, { value: 2, label: 'View/Edit', buttonClass: 'btn-bg-blue' }]
    });

    closed$.subscribe(async value => {
      if (!value) return;
      if (value === 1) {
        await this.mergesupplierRequest(request);
      }
      else {
        const navItem = this.navTab.getNavItemFromTitle('Supplier/Vender Management');
        if (navItem) {
          this.navTab.addTab({ title: navItem.title, icon: navItem.icon, componentKey: navItem.componentKey, data: { SupplierRequestID: request.SupplierRequestID } });
        }
      }
    })
  }
  async mergesupplierRequest(request: any): Promise<void> {
    try {
      // ---- 1. Parse full request payload ----
      let reqPayload: any = request;
      try {
        if (request?.SupplierRequestJSON) {
          reqPayload = { ...request, ...JSON.parse(request.SupplierRequestJSON) };
        }
      } catch { }

      // Normalize SupplierID (blank = new)
      const supplierId =
        reqPayload?.SupplierID && reqPayload.SupplierID.toString().trim() !== ''
          ? Number(reqPayload.SupplierID)
          : null;

      // ---- 2. Build remapped DB payload ----
      const merged: any = {};

      this.supplierFieldMap.forEach(m => {
        const val = reqPayload?.[m.req];

        // Only send non-empty values
        if (val !== undefined && val !== null && val !== '') {
          merged[m.db] = val;
        }
      });

      // ---- 3. NEW supplier ----
      if (!supplierId) {
        merged.SupplierID = null;
      } else {
        merged.SupplierID = supplierId;
      }
      merged.SupplierContact = merged.TraderContactFirstName + ' ' + merged.TraderContactLastName;
      merged.TrafficContact = merged.TrafficContactFirstName + ' ' + merged.TrafficContactLastName
      if (request.OrganicCertification.length > 0) {

      }
      //await lastValueFrom(this.web.getWebCall('venders', '/merge_supplier', { body: { supplierJson: JSON.stringify(merged), SupplierRequestID: request.SupplierRequestID } }))

      if (supplierId) {
        this.message.success(`Updated Supplier ${merged.SupplierName}`)
      }
      else {
        this.message.success(`Created Supplier ${merged.SupplierName}`)
      }
      this.Refresh();
    } catch (err) {
      console.error(err);
    }
  }
  async createHtml(request: any, opts: { titlePrefix?: string; } = {}): Promise<string> {

    const styles = `
  <style>
    :root{
      --bg: #ffffff;
      --panel: #f6f7f9;
      --text: #111;
      --muted: rgba(0,0,0,.55);
      --border: rgba(0,0,0,.10);

      --changed-bg: #fff7e6;
      --changed-bd: #ffd591;

      --new-bg: #e6f7ff;
      --new-bd: #91d5ff;

      --shadow: 0 8px 22px rgba(0,0,0,.10);
    }

    .prompt-container{
      width: 96vw;
      height:80vh;
    }

    .prompt-head{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 14px;
      margin-bottom: 10px;
    }

    .prompt-title{
      font-size: 20px;
      font-weight: 800;
      margin-bottom: 4px;
      letter-spacing: .2px;
    }

    .prompt-subtitle{
      font-size: 13px;
      color: var(--muted);
      margin-bottom: 0;
    }

    .meta-actions{
      display:flex;
      flex-wrap:wrap;
      gap: 8px;
      align-items:center;
      justify-content:flex-end;
    }

    .pill{
      display:inline-flex;
      align-items:center;
      gap: 6px;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 800;
      border: 1px solid var(--border);
      background: var(--panel);
      user-select:none;
      white-space:nowrap;
    }
    .pill.update{ background:#fff1b8; }
    .pill.new{ background:#bae7ff; }

    .toggle{
      display:inline-flex;
      align-items:center;
      gap: 6px;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 800;
      border: 1px solid var(--border);
      background: var(--bg);
      cursor:pointer;
      user-select:none;
    }
    .toggle input{ transform: translateY(1px); }

    .summary-bar{
      display:flex;
      flex-wrap:wrap;
      gap: 8px;
      margin: 10px 0 14px;
    }

    .summary-chip{
      background: var(--panel);
      border: 1px solid var(--border);
      border-radius: 10px;
      padding: 6px 10px;
      font-size: 12px;
      color: rgba(0,0,0,.75);
    }
    .summary-chip strong{ color: #111; }

    /* GRID: this is the big improvement */
    .sections-grid{
      display:grid;
      gap: 14px;
      grid-template-columns: repeat(4, minmax(240px, 1fr));
      align-items:start;
    }


    /* Responsive: drop down */
    @media (max-width: 1200px){
      .sections-grid{
        grid-template-columns: repeat(3, minmax(240px, 1fr));
      }
    }
    @media (max-width: 900px){
      .sections-grid{
        grid-template-columns: repeat(2, minmax(240px, 1fr));
      }
    }
    @media (max-width: 650px){
      .sections-grid{
        grid-template-columns: 1fr;
      }
    }

    .section-card{
      background: var(--bg);
      border: 1px solid var(--border);
      border-radius: 12px;
      box-shadow: var(--shadow);
      overflow:hidden;
    }

    .section-header{
      position: sticky;
      top: 0;
      z-index: 1;
      background: linear-gradient(#fff, #fff);
      padding: 10px 12px;
      border-bottom: 1px solid var(--border);
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 10px;
    }

    .section-title{
      font-weight: 900;
      font-size: 14px;
      color: #333;
      letter-spacing: .2px;
    }

    .section-count{
      font-size: 11px;
      font-weight: 800;
      color: rgba(0,0,0,.55);
      background: var(--panel);
      border: 1px solid var(--border);
      padding: 2px 8px;
      border-radius: 999px;
      white-space:nowrap;
    }

    .rows{
      padding: 10px 10px 12px;
      display:flex;
      flex-direction:column;
      gap: 8px;
    }

    .row{
      display:grid;
      grid-template-columns: 140px 1fr;
      gap: 10px;
      padding: 8px 10px;
      border-radius: 10px;
      background: var(--panel);
      border: 1px solid transparent;
    }

    .row.changed{
      background: var(--changed-bg);
      border-color: var(--changed-bd);
    }

    .row.new{
      background: var(--new-bg);
      border-color: var(--new-bd);
    }

    .label{
      font-weight: 800;
      color: rgba(0,0,0,.72);
      font-size: 12px;
      line-height: 1.2;
      align-self:center;
    }

    .value{
      color: #111;
      font-size: 13px;
    }

    .muted{
      color: var(--muted);
      font-style: italic;
    }

    .change{
      display:flex;
      align-items:center;
      gap: 8px;
      flex-wrap:wrap;
    }

    .old{
      color: rgba(0,0,0,.70);
    }
    .arrow{
      opacity:.55;
      font-weight: 900;
    }
    .newv{
      font-weight: 900;
    }

    .mono{
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      font-size: 12px;
    }

    /* Hide unchanged when toggle is on */
    .hide-unchanged .row:not(.changed){ display:none; }

    .file-row{
      display:flex;
      flex-direction:column;
      gap:4px;
    }
    .file-meta{
      font-size:12px;
      color: rgba(0,0,0,.70);
    }
    .file-url{
      font-size:12px;
      color: rgba(0,0,0,.55);
      word-break: break-all;
    }
    .badge{
      display:inline-flex;
      align-items:center;
      gap:6px;
      font-size:11px;
      font-weight:800;
      padding:2px 8px;
      border-radius:999px;
      border:1px solid var(--border);
      background: var(--panel);
    }
    .badge.file { background:#e6fffb; border-color:#87e8de; }

  </style>
  `;



    // -------- helpers --------
    const esc = (v: any) => {
      const s = (v ?? '').toString();
      return s
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    };

    const normalizeEmpty = (v: any) => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'string' && v.trim() === '') return '';
      return v;
    };

    const display = (v: any) => {
      const nv = normalizeEmpty(v);
      return nv === '' ? `<span class="muted">—</span>` : esc(nv);
    };

    const changed = (a: any, b: any) => {
      const na = (normalizeEmpty(a) ?? '').toString().trim();
      const nb = (normalizeEmpty(b) ?? '').toString().trim();
      return na !== nb;
    };

    // Pull request payload from SupplierRequestJSON if present
    let reqPayload: any = request;
    try {
      if (request?.SupplierRequestJSON) {
        reqPayload = { ...request, ...JSON.parse(request.SupplierRequestJSON) };
      }
    } catch {
      // ignore parse errors; fall back to request directly
    }

    // SupplierID may be '' (string)
    const supplierIdRaw = normalizeEmpty(reqPayload?.SupplierID);
    const supplierId = supplierIdRaw === '' ? null : Number(supplierIdRaw);

    const parseOrganicFiles = (v: any): any[] => {
      if (!v) return [];
      // Already an array
      if (Array.isArray(v)) return v;

      // Sometimes the JSON payload might contain it as a stringified array
      if (typeof v === 'string') {
        try {
          const parsed = JSON.parse(v);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }

      return [];
    };

    const organicFiles = parseOrganicFiles(reqPayload?.OrganicCertification ?? request?.OrganicCertification);

    // A small formatter for sizes
    const formatBytes = (bytes: any) => {
      const n = Number(bytes);
      if (!Number.isFinite(n) || n <= 0) return '';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0, v = n;
      while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
      return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    };

    // Destination folder preview (NEW vs UPDATE)
    const destFolder = supplierId
      ? `suppliers/supplier-${supplierId}/`
      : `suppliers/supplier-(new)/`;

    // Fetch current supplier row if SupplierID exists
    let current: any | null = null;
    if (supplierId) {
      const pull = await lastValueFrom(
        this.web.getWebCall('venders', 'supplier', {
          params: [{ value: supplierId, name: 'SupplierID' }]
        })
      );

      const mapped = pull.map((item: any) => {
        return {
          ...item,
          TraderContactFirstName: item.SupplierContact?.split(' ')[0] ?? '',
          TraderContactLastName: item.SupplierContact?.split(' ').slice(1).join(' ') ?? '',
          TrafficContactFirstName: item.TrafficContact?.split(' ')[0] ?? '',
          TrafficContactLastName: item.TrafficContact?.split(' ').slice(1).join(' ') ?? '',
        };
      });

      current = mapped?.[0] ?? null;
    }

    // -------- Field mapping: Request -> Supplier table columns --------
    const fields = [
      { section: 'Basic Info', label: 'Company Name', reqKey: 'CompanyName', curKey: 'SupplierName' },
      { section: 'Basic Info', label: 'Country', reqKey: 'Country', curKey: 'SupplierContactCountry' },
      { section: 'Basic Info', label: 'FDA Registration', reqKey: 'FDARegistration', curKey: 'SupplierFDA' },
      { section: 'Basic Info', label: 'FLO ID', reqKey: 'FLOID', curKey: 'SupplierFLOID' },
      { section: 'Basic Info', label: 'FTUSA ID', reqKey: 'FTUSAID' },
      { section: 'Basic Info', label: 'PSS1 Email', reqKey: 'PSS1Email', curKey: 'AppRejEmailNo1' },
      { section: 'Basic Info', label: 'PSS2 Email', reqKey: 'PSS2Email', curKey: 'AppRejEmailNo2' },
      { section: 'Basic Info', label: 'PSS3 Email', reqKey: 'PSS3Email', curKey: 'AppRejEmailNo3' },

      { section: 'Trader Contact', label: 'Trader First Name', reqKey: 'TraderContactFirstName', curKey: 'TraderContactFirstName' },
      { section: 'Trader Contact', label: 'Trader Last Name', reqKey: 'TraderContactLastName', curKey: 'TraderContactLastName' },
      { section: 'Trader Contact', label: 'Trader Email', reqKey: 'TraderContactEmail', curKey: 'SupplierEmail' },
      { section: 'Trader Contact', label: 'Trader Phone', reqKey: 'TraderPhone', curKey: 'SupplierTel' },
      { section: 'Trader Contact', label: 'Street', reqKey: 'TraderStreetAddress', curKey: 'SupplierStreet' },
      { section: 'Trader Contact', label: 'City', reqKey: 'TraderCity', curKey: 'SupplierCity' },
      { section: 'Trader Contact', label: 'State', reqKey: 'TraderState', curKey: 'SupplierState' },
      { section: 'Trader Contact', label: 'Postal Code', reqKey: 'TraderPostalCode', curKey: 'SupplierZip' },
      { section: 'Trader Contact', label: 'Trader Messenger', reqKey: 'TraderMessenger', curKey: 'SupplierContactSkypeHandle' },
      { section: 'Trader Contact', label: 'Trader Secondary Email 1', reqKey: 'TraderSecondaryEmail1', curKey: 'TraderSecondaryEmail1' },
      { section: 'Trader Contact', label: 'Trader Secondary Email 2', reqKey: 'TraderSecondaryEmail2', curKey: 'TraderSecondaryEmail2' },
      { section: 'Trader Contact', label: 'Trader Secondary Email 3', reqKey: 'TraderSecondaryEmail3', curKey: 'TraderSecondaryEmail3' },

      { section: 'Traffic', label: 'Traffic First Name', reqKey: 'TrafficFirstName', curKey: 'TrafficContactFirstName' },
      { section: 'Traffic', label: 'Traffic Last Name', reqKey: 'TrafficLastName', curKey: 'TrafficContactLastName' },
      { section: 'Traffic', label: 'Traffic Email', reqKey: 'TrafficEmail', curKey: 'SupplierTrafficEmail' },
      { section: 'Traffic', label: 'Traffic Phone', reqKey: 'TrafficPhone', curKey: 'TrafficNo' },
      { section: 'Traffic', label: 'Traffic Secondary Email 1', reqKey: 'TrafficSecondaryEmail1', curKey: 'TrafficSecondaryEmail1' },
      { section: 'Traffic', label: 'Traffic Secondary Email 2', reqKey: 'TrafficSecondaryEmail2', curKey: 'TrafficSecondaryEmail2' },
      { section: 'Traffic', label: 'Traffic Secondary Email 3', reqKey: 'TrafficSecondaryEmail3', curKey: 'TrafficSecondaryEmail3' },


      { section: 'Price Fix', label: 'Price Fix First Name', reqKey: 'PriceFixFirstName', curKey: 'PriceFixFirstName' },
      { section: 'Price Fix', label: 'Price Fix Last Name', reqKey: 'PriceFixLastName', curKey: 'PriceFixLastName' },
      { section: 'Price Fix', label: 'Price Fix Email', reqKey: 'PriceFixEmail', curKey: 'PriceFixEmail' },
      { section: 'Price Fix', label: 'Price Fix Phone', reqKey: 'PriceFixPhone', curKey: 'PriceFixPhone' },
      { section: 'Price Fix', label: 'Price Fix Secondary Email 1', reqKey: 'PriceFixSecondaryEmail1', curKey: 'PriceFixSecondaryEmail1' },
      { section: 'Price Fix', label: 'Price Fix Secondary Email 2', reqKey: 'PriceFixSecondaryEmail2', curKey: 'PriceFixSecondaryEmail2' },
      { section: 'Price Fix', label: 'Price Fix Secondary Email 3', reqKey: 'PriceFixSecondaryEmail3', curKey: 'PriceFixSecondaryEmail3' },

    ];

    const companyName = reqPayload?.CompanyName ?? request?.CompanyName ?? '';
    const mode: 'update' | 'new' = supplierId ? 'update' : 'new';
    const prefix = opts.titlePrefix ?? 'Supplier Request';

    const title = mode === 'update'
      ? `${prefix} — Update: ${companyName}`
      : `${prefix} — New: ${companyName}`;  

    const subtitle = mode === 'update'
      ? `Comparing current Supplier: ${current?.SupplierName ?? ''} to requested changes`
      : `No SupplierID on request — creating a new supplier`;

    const pill = mode === 'update'
      ? `<span class="pill update">UPDATE</span>`
      : `<span class="pill new">NEW</span>`;

    // -------- Build rows --------
    // -------- Build rows grouped by section (with counts) --------
    type FieldDef = { section: string; label: string; reqKey: string; curKey?: string };

    const groupBySection = (arr: FieldDef[]) => {
      const map = new Map<string, FieldDef[]>();
      for (const f of arr) {
        if (!map.has(f.section)) map.set(f.section, []);
        map.get(f.section)!.push(f);
      }
      return map;
    };

    const pretty = (v: any) => {
      const nv = normalizeEmpty(v);
      if (nv === '') return `<span class="muted">—</span>`;

      if (typeof nv === 'boolean') return nv ? 'Yes' : 'No';
      if (typeof nv === 'number') return esc(nv);

      // objects/arrays: compact JSON (but readable)
      if (typeof nv === 'object') {
        try {
          const s = JSON.stringify(nv, null, 0);
          return `<span class="mono">${esc(s)}</span>`;
        } catch {
          return `<span class="mono">${esc(String(nv))}</span>`;
        }
      }

      // long strings: soften but keep full
      const s = String(nv);
      if (s.length > 120) {
        return `${esc(s.slice(0, 120))}<span class="muted">…</span>`;
      }
      return esc(s);
    };

    const sections = groupBySection(fields as any);

    // Calculate summary counts
    let totalFields = 0;
    let totalChanged = 0;
    let totalFilledNew = 0;

    for (const [, list] of sections) {
      for (const f of list) {
        totalFields++;
        const toVal = reqPayload?.[f.reqKey];
        const fromVal = current ? current?.[f.curKey ?? ''] : undefined;

        if (mode === 'new') {
          if (normalizeEmpty(toVal) !== '') totalFilledNew++;
        } else {
          if (changed(fromVal, toVal)) totalChanged++;
        }
      }
    }

    let sectionsHtml = '';

    for (const [sectionName, list] of sections) {
      // section-level counts
      let secTotal = 0;
      let secChanged = 0;
      let secFilled = 0;

      let rows = '';

      for (const f of list) {
        secTotal++;
        const toVal = reqPayload?.[f.reqKey];
        const fromVal = current ? current?.[f.curKey ?? ''] : undefined;
        const isChanged = current ? changed(fromVal, toVal) : true;

        if (mode === 'new') {
          if (normalizeEmpty(toVal) === '') continue; // keep NEW mode clean
          secFilled++;

          rows += `
        <div class="row new">
          <div class="label">${esc(f.label)}</div>
          <div class="value">${pretty(toVal)}</div>
        </div>
      `;
          continue;
        }

        // update mode
        if (isChanged) secChanged++;

        rows += `
      <div class="row ${isChanged ? 'changed' : ''}">
        <div class="label">${esc(f.label)}</div>
        <div class="value">
          <div class="change">
            <span class="old">${pretty(fromVal)}</span>
            <span class="arrow">→</span>
            <span class="newv">${pretty(toVal)}</span>
          </div>
        </div>
      </div>
    `;
      }

      // If NEW mode and section has no rows, skip entire card
      if (mode === 'new' && secFilled === 0) continue;

      const countText =
        mode === 'new'
          ? `${secFilled}/${secTotal} provided`
          : `${secChanged}/${secTotal} changed`;

      sectionsHtml += `
    <div class="section-card">
      <div class="section-header">
        <div class="section-title">${esc(sectionName)}</div>
        <div class="section-count">${esc(countText)}</div>
      </div>
      <div class="rows">
        ${rows || `<div class="muted">No values</div>`}
      </div>
    </div>
  `;
    }

    let filesHtml = '';
    if (organicFiles.length) {
      let rows = '';

      for (const f of organicFiles) {
        const name = f?.file || f?.name || 'attachment';
        const size = formatBytes(f?.size);
        const url = (f?.url || '').toString();

        // If you always want OrganicCert.pdf regardless of name:
        // const dest = `${destFolder}OrganicCert.pdf`;

        // Or preserve the original name:
        const dest = `${destFolder}${name}`;

        rows += `
      <div class="row new">
        <div class="label">${esc(name)}</div>
        <div class="value file-row">
          <div class="file-meta">
            Will be copied on <strong>Auto Approve</strong>
            ${size ? `• ${esc(size)}` : ''}
          </div>
          <div class="file-meta">Destination: <span class="mono">${esc(dest)}</span></div>
          ${url ? `<div class="file-url">Source: ${esc(url)}</div>` : ''}
        </div>
      </div>
    `;
      }
    }

    // -------- Final HTML w/ toggle + summary --------
    const summaryLine =
      mode === 'new'
        ? `<div class="summary-chip"><strong>${totalFilledNew}</strong> fields provided <span class="muted">(${totalFields} total)</span></div>`
        : `<div class="summary-chip"><strong>${totalChanged}</strong> fields changed <span class="muted">(${totalFields} total)</span></div>`;

    const filesLine = organicFiles.length
      ? `<div class="summary-chip">
       <span class="badge file">FILE</span>
       <strong>${organicFiles.length}</strong> file${organicFiles.length > 1 ? 's' : ''} will be saved to
       <span class="mono">${esc(destFolder)}</span>
     </div>`
      : '';


    const script =
      mode === 'update'
        ? `
    <script>
      (function(){
        const root = document.getElementById('sr_root');
        const cb = document.getElementById('onlyChanged');
        if(!root || !cb) return;
        cb.addEventListener('change', function(){
          root.classList.toggle('hide-unchanged', cb.checked);
        });
      })();
    </script>
    `
        : '';

      return `
    ${styles}
    <div id="sr_root" class="prompt-container">
      <div class="prompt-head">
        <div>
          <div class="prompt-title">${esc(title)}
            ${mode === 'update'
          ? `<span class="pill update">UPDATE</span>`
          : `<span class="pill new">NEW</span>`}
          </div>
          <div class="prompt-subtitle">${esc(subtitle)}</div>
        </div>
      </div>

      <div class="summary-bar">
        ${summaryLine}
        ${filesLine}
      </div>

      <div class="sections-grid">
        ${sectionsHtml}
      </div>
    </div>
    ${script}
  `;
  }

}
